import {
  ReportDesignerItemCustom,
  ReportDesignerItemGroupHorizontal,
  ReportDesignerItemGroupVertical,
  ReportDesignerItemImageConfig,
  ReportDesignerItemSpacer,
  ReportDesignerItemTab,
  ReportDesignerItemTitle,
  ReportDesignItemChecklistRendered,
  ReportDesignItemCheckRendered,
  ReportDesignItemConfig,
  ReportDesignItemCustomerSignature,
  ReportDesignItemGrid,
  ReportDesignItemSignature,
  ReportDesignItemSelectbox,
  ReportDesignItemText,
  ReportDesignItemTextAreaRendered
} from "src/app/modules/bootstrap/modules/report/report-design-item";
import { dateUtil } from "src/app/support/date-util";
import { bindFormGridProperty } from "src/app/modules/bootstrap/modules/report/report-designer/pipes/report-item-form-property-bind.pipe";
import { compact, get } from "lodash-es";
import {
  getCheckListColumns,
  getCheckListGroups,
  getCheckListRows
} from "src/app/modules/bootstrap/modules/report/report-support";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { ReportFormRendererImageComponentValueType } from "src/app/modules/bootstrap/modules/report/report-form-renderer/report-form-renderer-image/report-form-renderer-image.component";
import { UserBackend } from "src/app/backend/api/user.backend";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { catchError, lastValueFrom, map, of } from "rxjs";
import { stripHTMLTags } from "src/app/support/string";
import { MemberLight } from "src/app/backend/models/member/member";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";

type ReportTemplateRendererProviders = {
  storageBackend: StorageBackend;
  userBackend: UserBackend;
  memberBackend: MemberBackend;
};

type ReportPdfPrintBaseItem = {
  name: string;
};

type ReportPdfPrintCustomItem = ReportPdfPrintBaseItem & {
  type: "custom";
  html: string;
};

type ReportPdfPrintHeaderItem = ReportPdfPrintBaseItem & {
  type: "header";
  zoom: string;
  logo: string;
  title: string;
  subTitle: string;
  info: string;
};

type ReportPdfPrintFooterItem = ReportPdfPrintBaseItem & {
  type: "footer";
  lines: string[];
};

type ReportPdfPrintGroupVerticalItem = ReportPdfPrintBaseItem & {
  type: "group-vertical";
  cols: string;
  title: string;
  items: ReportPdfPrintItem[];
};

type ReportPdfPrintGroupHorizontalItem = ReportPdfPrintBaseItem & {
  type: "group-horizontal";
  cols: string;
  title: string;
  items: ReportPdfPrintItem[];
};

type ReportPdfPrintSignatureAlign = "left" | "center" | "right";

type ReportPdfPrintCustomerSignatureItem = ReportPdfPrintBaseItem & {
  type: "customer-signature";
  firmaDigitaleDelClienteLabel: string;
  align: ReportPdfPrintSignatureAlign;
};

type ReportPdfPrintSignatureItem = ReportPdfPrintBaseItem & {
  type: "signature";
  align: ReportPdfPrintSignatureAlign;
  html: string[] | undefined;
};

type ReportPdfPrintTableItem = ReportPdfPrintBaseItem & {
  type: "table";
  cols: string;
  items: Array<{
    title: string;
    property: string;
    dataType: string;
    value: string;
  }>;
};

type ReportPdfPrintTextItem = ReportPdfPrintBaseItem & {
  type: "text";
  title: string;
  text: string;
};

type ReportPdfPrintSelectboxItem = ReportPdfPrintBaseItem & {
  type: "selectbox";
  title: string;
  text: string;
};

type ReportPdfPrintSpacerItem = ReportPdfPrintBaseItem & {
  type: "spacer";
  size: string;
  dashed: boolean;
  separator: boolean;
};

type ReportPdfPrintTitleItemFontWeight =
  | "extrabold"
  | "semibold"
  | "medium"
  | "normal"
  | "light"
  | "extralight"
  | "thin";

type ReportPdfPrintTitleItemFontAlignment = "left" | "center" | "right";

type ReportPdfPrintTitleItemSize = "h5" | "h4" | "h3" | "h2" | "h1";

type ReportPdfPrintTitleItem = ReportPdfPrintBaseItem & {
  type: "title";
  title: string;
  size: ReportPdfPrintTitleItemSize;
  alignment: ReportPdfPrintTitleItemFontAlignment;
  fontWeight: ReportPdfPrintTitleItemFontWeight;
};

type ReportPdfPrintImageItem = ReportPdfPrintBaseItem & {
  type: "image";
  data: string;
  caption: string;
  align: "left" | "center" | "right";
  size: number;
};

type ReportPdfPrintTabItem = {
  title: string;
  items: ReportPdfPrintItem[];
};

type ReportPdfPrintTabsItem = ReportPdfPrintBaseItem & {
  type: "tab";
  title: string;
  tabs: ReportPdfPrintTabItem[];
};

export type ReportPdfPrintItem =
  | ReportPdfPrintHeaderItem
  | ReportPdfPrintFooterItem
  | ReportPdfPrintGroupVerticalItem
  | ReportPdfPrintGroupHorizontalItem
  | ReportPdfPrintSignatureItem
  | ReportPdfPrintCustomerSignatureItem
  | ReportPdfPrintTableItem
  | ReportPdfPrintTextItem
  | ReportPdfPrintSelectboxItem
  | ReportPdfPrintSpacerItem
  | ReportPdfPrintImageItem
  | ReportPdfPrintTitleItem
  | ReportPdfPrintCustomItem
  | ReportPdfPrintTabsItem;

export type ReportPdfPrintObject = Array<ReportPdfPrintItem>;

export async function renderReportTemplateToPdfObject(
  template: ReportDesignItemConfig[],
  dataSource: Record<string, any>,
  providers: ReportTemplateRendererProviders,
  options: {
    onlyTextWithValue?: boolean;
    useFakeTextData?: boolean;
  }
): Promise<ReportPdfPrintObject> {
  const result: ReportPdfPrintObject = compact(
    await Promise.all(
      template.map(async (templateItem) => {
        let item: ReportPdfPrintItem | undefined;

        try {
          switch (templateItem.type) {
            case "checklist": {
              item = await renderReportCheckList(templateItem);
              break;
            }
            case "check": {
              item = await renderReportCheck(templateItem);
              break;
            }
            case "custom": {
              item = await renderReportCustom(
                <ReportDesignerItemCustom>templateItem
              );
              break;
            }
            case "tab": {
              item = await renderReportTab(
                <ReportDesignerItemTab>templateItem,
                dataSource,
                providers,
                options
              );

              if (!item.tabs || item.tabs.length === 0) {
                item = undefined;
              }

              break;
            }
            case "text": {
              item = await renderReportText(templateItem);
              break;
            }
            case "selectbox": {
              item = await renderReportSelectbox(
                <ReportDesignItemSelectbox>templateItem
              );
              break;
            }
            case "textarea": {
              item = await renderReportTextArea(templateItem);
              break;
            }
            case "title": {
              item = await renderReportTitle(templateItem);
              break;
            }
            case "image": {
              item = await renderReportImage(
                <ReportDesignerItemImageConfig>(<any>templateItem),
                providers
              );
              break;
            }
            case "spacer": {
              item = await renderReportSpacer(templateItem);
              break;
            }
            case "table": {
              item = await renderReportTable(
                <ReportDesignItemGrid>templateItem,
                dataSource
              );
              break;
            }
            case "customer-signature": {
              item = await renderReportCustomerSignature(
                <ReportDesignItemCustomerSignature>templateItem
              );
              break;
            }
            case "signature": {
              item = await renderReportSignature(
                <ReportDesignItemSignature>templateItem,
                dataSource,
                providers
              );
              break;
            }
            case "group-vertical": {
              item = await renderReportGroupVertical(
                <ReportDesignerItemGroupVertical>templateItem,
                dataSource,
                providers
              );
              break;
            }
            case "group":
            case "group-horizontal": {
              item = await renderReportGroupHorizontal(
                <ReportDesignerItemGroupVertical>templateItem,
                dataSource,
                providers
              );
              break;
            }
            default: {
              break;
            }
          }
        } catch (ex) {
          console.dir(templateItem);
          console.dir(ex);
          throw ex;
        }

        return item;
      })
    )
  );

  return result;
}

async function renderReportGroupVertical(
  item: ReportDesignerItemGroupVertical,
  dataSource: Record<string, any>,
  providers: ReportTemplateRendererProviders
): Promise<ReportPdfPrintGroupVerticalItem> {
  return {
    type: "group-vertical",
    name: item.name,
    title: item.config.title,
    items: await renderReportTemplateToPdfObject(
      item.config.group,
      dataSource,
      providers,
      {}
    ),
    cols: item.config.cols
  };
}

async function renderReportGroupHorizontal(
  item: ReportDesignerItemGroupHorizontal,
  dataSource: Record<string, any>,
  providers: ReportTemplateRendererProviders
): Promise<ReportPdfPrintGroupHorizontalItem> {
  return {
    type: "group-horizontal",
    name: item.name,
    title: item.config.title,
    items: await renderReportTemplateToPdfObject(
      item.config.group,
      dataSource,
      providers,
      {}
    ),
    cols: item.config.cols
  };
}

async function renderReportCustomerSignature(
  item: ReportDesignItemCustomerSignature
): Promise<ReportPdfPrintCustomerSignatureItem> {
  return {
    type: "customer-signature",
    name: item.name!,
    align: <ReportPdfPrintSignatureAlign>item.config.align,
    firmaDigitaleDelClienteLabel: sntTranslate(
      marker("Firma smart del cliente")
    )
  };
}

async function renderReportSignature(
  item: ReportDesignItemSignature,
  dataSource: Record<string, any>,
  providers: ReportTemplateRendererProviders
): Promise<ReportPdfPrintSignatureItem> {
  let html: string[] | undefined;

  if (item.config?.user) {
    switch (item.config.user) {
      case "authors": {
        html = compact(
          await Promise.all<string | undefined>(
            dataSource.authors || dataSource.staff
              ? (
                  dataSource.authors ||
                  dataSource.staff?.map((x: { id: string }) => x.id) ||
                  []
                ).map((authorId: string) =>
                  lastValueFrom(
                    providers.memberBackend
                      .loadLight(authorId)
                      .pipe(
                        map((memberLight: MemberLight) => memberLight.signature)
                      )
                  )
                ) || []
              : []
          )
        );
        break;
      }
      case "current-user": {
        const user = await lastValueFrom(providers.userBackend.loadMe());
        html = compact([user.signature || ""]);
        break;
      }
      default: {
        const member = await lastValueFrom(
          providers.memberBackend.loadLight(item.config.user)
        );
        html = compact([member?.signature || ""]);
        break;
      }
    }
  }

  return {
    type: "signature",
    name: item.name,
    align: <ReportPdfPrintSignatureAlign>item.config.align,
    html: html
  };
}

async function renderReportTable(
  item: ReportDesignItemGrid,
  dataSource: Record<string, any>
): Promise<ReportPdfPrintTableItem> {
  return {
    type: "table",
    name: item.name,
    cols: item.config.cols,
    items:
      item.config.items?.map((item: any) => ({
        title: item.title,
        value: bindFormGridProperty(item.property, dataSource, item.dataType),
        dataType: item.dataType,
        property: item.property
      })) || []
  };
}

async function renderReportSpacer(
  item: ReportDesignerItemSpacer
): Promise<ReportPdfPrintSpacerItem> {
  return {
    type: "spacer",
    name: item.name,
    size: item.config.size,
    dashed: item.config.dashed,
    separator: item.config.separator
  };
}

async function renderReportImage(
  item: ReportDesignerItemImageConfig,
  providers: ReportTemplateRendererProviders
): Promise<ReportPdfPrintImageItem> {
  if (!item.value) {
    return {
      type: "image",
      data: "",
      name: item.name,
      caption: "",
      align: "left",
      size: 3
    };
  }

  const parsed: ReportFormRendererImageComponentValueType = JSON.parse(
    item.value
  );

  const downloadUrl = parsed?.storageId
    ? await lastValueFrom(
        providers.storageBackend
          .download(parsed.storageId)
          .pipe(catchError(() => of("")))
      )
    : "";

  return {
    type: "image",
    data: downloadUrl,
    name: item.name,
    caption: parsed.caption,
    align: parsed.align || "left",
    size: parsed.size || 3
  };
}

async function renderReportTitle(
  item: ReportDesignerItemTitle
): Promise<ReportPdfPrintTitleItem> {
  return {
    type: "title",
    title: item.config.title,
    fontWeight: <ReportPdfPrintTitleItemFontWeight>item.config.fontWeight,
    alignment: <ReportPdfPrintTitleItemFontAlignment>item.config.alignment,
    size: <ReportPdfPrintTitleItemSize>item.config.size,
    name: item.name
  };
}

async function renderReportTab(
  item: ReportDesignerItemTab,
  dataSource: Record<string, any>,
  providers: ReportTemplateRendererProviders,
  options: {
    onlyTextWithValue?: boolean;
    useFakeTextData?: boolean;
  }
): Promise<ReportPdfPrintTabsItem> {
  let tabs: ReportPdfPrintTabItem[] = [];

  for (let tab of item.config.tabs) {
    const items = await renderReportTemplateToPdfObject(
      tab.items,
      dataSource,
      providers,
      options
    );
    if (items.length > 0) {
      tabs.push({
        title: tab.name,
        items
      });
    }
  }

  return {
    type: "tab",
    name: item.config.name,
    title: item.title,
    tabs
  };
}

async function renderReportCheck(
  item: ReportDesignItemCheckRendered
): Promise<any> {
  return {
    type: "check",
    title: item.config.text,
    value: item.value
  };
}

async function renderReportCheckList(
  item: ReportDesignItemChecklistRendered
): Promise<any> {
  return {
    type: "checklist",
    rows: getCheckListRows(item.config.rows),
    columns: getCheckListColumns(item.config.columns),
    groups: getCheckListGroups(item.config.groups),
    value: JSON.parse(item.value || "{}")
  };
}

async function renderReportTextArea(
  item: ReportDesignItemTextAreaRendered
): Promise<any> {
  return {
    type: "textarea",
    title: stripHTMLTags(item.config.text || ""),
    value: item.value
  };
}

async function renderReportSelectbox(
  item: ReportDesignItemSelectbox
): Promise<ReportPdfPrintSelectboxItem> {
  const value = get(item, "value");

  return {
    type: "selectbox",
    name: item.config.name ?? "",
    title: stripHTMLTags(item.config.title || ""),
    text: (value ?? "") as string
  };
}

async function renderReportText(
  item: ReportDesignItemText
): Promise<ReportPdfPrintTextItem> {
  const value = get(item, "value"); // || item.config.value;

  if (!value) {
    return {
      name: item.config.name,
      type: "text",
      title: stripHTMLTags(item.config.title || ""),
      text: ""
    };
  }

  let valueFormatted;

  switch (item.config.dataType) {
    case "datetime": {
      valueFormatted = dateUtil(value).format("DD/MM/YYYY HH:mm");
      break;
    }
    case "date": {
      valueFormatted = dateUtil(value).format("DD/MM/YYYY");
      break;
    }
    default: {
      valueFormatted = value;
      break;
    }
  }

  return {
    name: item.config.name,
    type: "text",
    title: stripHTMLTags(item.config.title || ""),
    text: valueFormatted
  };
}

async function renderReportCustom(
  item: ReportDesignerItemCustom
): Promise<ReportPdfPrintCustomItem> {
  if (!item.baseConfig.html) {
    return {
      name: item.config.name,
      type: "custom",
      html: ""
    };
  }

  const html = item.baseConfig?.html
    ? await item.baseConfig.html(item)
    : "Custom html empty!";

  return {
    name: item.config.name,
    type: "custom",
    html: html
  };
}
